/**
 * ClientQcRedoDialog (BLU-1462)
 *
 * A Hand over client found faulty garments after the hand over. The
 * coordinator picks them and writes what is wrong; the server reopens the
 * same order and makes one redo per picked garment, which joins the next
 * batch that includes redos and goes back to the hand over bench. No
 * preflight read: the server re-checks everything and names the reason when
 * it refuses, which this dialog shows inline.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { formatOrderNumber } from '@threadheads/shared';
import { useCreateClientQcRedo } from '../hooks/useStatusOverride';
import {
  CLIENT_QC_REDO_MAX_GARMENTS,
  CLIENT_QC_REDO_NOTE_MAX,
  QC_REDO_REFUSAL_KEYS,
  type ClientQcRedoRefusal,
} from '../lib/client-qc-redo';

export interface ClientQcRedoGarmentOption {
  id: string;
  sku: string;
  productName: string;
  size: string | null;
  color: string | null;
}

interface ClientQcRedoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderName: string;
  locationId: string;
  /** The order's SHIPPED printed garments (qcRedoEligibleGarments). */
  garments: ClientQcRedoGarmentOption[];
  /** After a reopen: the page switches to Active and searches the order. */
  onDone?: (orderName: string) => void;
}

export function ClientQcRedoDialog({
  open,
  onOpenChange,
  orderId,
  orderName,
  locationId,
  garments,
  onDone,
}: ClientQcRedoDialogProps) {
  const { t } = useTranslation('production');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState<ClientQcRedoRefusal | null>(null);
  // onDone moves the tracker to a new list query, which fetches by itself.
  const mutation = useCreateClientQcRedo({ listRefetchedByCaller: !!onDone });
  const isPending = mutation.isPending;

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setNote('');
      setRefusal(null);
    }
  }, [open]);

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const count = selected.size;
  const trimmedNote = note.trim();
  const canSubmit = count > 0 && count <= CLIENT_QC_REDO_MAX_GARMENTS && trimmedNote.length > 0 && !isPending;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setRefusal(null);
    try {
      const result = await mutation.mutateAsync({
        prodOrderId: orderId,
        locationId,
        // Keep the list order, so a retry sends the same set.
        garmentUnitIds: garments.filter((g) => selected.has(g.id)).map((g) => g.id),
        note: trimmedNote,
      });
      if (!result.ok) {
        setRefusal(result.reason);
        return;
      }
      toast.success(t('tracker.clientQcRedo.done', { name: formatOrderNumber(orderName) }));
      onOpenChange(false);
      onDone?.(orderName);
    } catch (error) {
      toast.error(t('tracker.clientQcRedo.failed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('tracker.clientQcRedo.title')}</DialogTitle>
          <DialogDescription>{formatOrderNumber(orderName)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm">{t('tracker.clientQcRedo.body')}</p>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('tracker.clientQcRedo.garmentsLabel')}</legend>
            {garments.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('tracker.clientQcRedo.noGarments')}</p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
                {garments.map((g) => {
                  const checkboxId = `qc-redo-${g.id}`;
                  return (
                    <li key={g.id}>
                      <label
                        htmlFor={checkboxId}
                        className="flex min-h-[44px] cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={checkboxId}
                          className="mt-0.5"
                          checked={selected.has(g.id)}
                          onCheckedChange={(v) => toggle(g.id, v === true)}
                          disabled={isPending}
                        />
                        <span className="min-w-0 flex-1 text-sm">
                          <span className="block break-all font-mono text-xs">{g.sku}</span>
                          <span className="block [overflow-wrap:anywhere]">
                            {[g.productName, g.size, g.color].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <div className="space-y-2">
            <label htmlFor="client-qc-redo-note" className="text-sm font-medium">
              {t('tracker.clientQcRedo.noteLabel')}
            </label>
            <Textarea
              id="client-qc-redo-note"
              placeholder={t('tracker.clientQcRedo.notePlaceholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[80px] text-base"
              maxLength={CLIENT_QC_REDO_NOTE_MAX}
              required
              disabled={isPending}
            />
            <div className="text-right text-[11px] text-muted-foreground">
              {note.length}/{CLIENT_QC_REDO_NOTE_MAX}
            </div>
          </div>

          {refusal && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300"
            >
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(QC_REDO_REFUSAL_KEYS[refusal])}</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="h-12" onClick={() => onOpenChange(false)} disabled={isPending}>
              {t('tracker.clientQcRedo.cancel')}
            </Button>
            <Button className="h-12" onClick={handleConfirm} disabled={!canSubmit}>
              {isPending
                ? t('tracker.clientQcRedo.redoing')
                : count > 0
                  ? t('tracker.clientQcRedo.confirm', { count })
                  : t('tracker.clientQcRedo.confirmNone')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
